import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { beforeSession, tscRules } from "./data/tscPlaybook";
import { TscAgentToggle, TscFooter, TscHeader } from "./tsc/TscChrome";
import { DEFAULT_TSC_DEMO, TscDemo, tscAsParam, tscDemoForAs, tscPickerHref, tscVoices } from "./tsc/tscDemos";

// Tractor Supply demo landing: Riley, the TSC CX crew. Styled after tractorsupply.com
// (see tsc/tsc.css). One way in, "Pick a customer"; the page never lists scenarios, so
// nothing suggests Riley only handles a fixed set of scripted calls.
// /tsc also hosts Mike, the same crew in a male voice: ?as=mike, picked with the toggle.

const VERIFIES: Record<TscDemo["verifyWith"], string> = {
  customerId: "Greets, verifies the customer ID and billing zip (the email for a password reset), and routes the call",
  email: "Greets, verifies the email and billing zip, and routes the call",
};

function crew(demo: TscDemo) {
  return [
    { name: "Orchestrator", d: VERIFIES[demo.verifyWith] },
    { name: "Order Cancellation", d: "Follows the cancellation SOP: cancel inside 59 minutes, or a store, warehouse or vendor request after that" },
    { name: "Recovery and Upsell", d: "After a real cancel, offers one cheaper pick and emails a payment link" },
    { name: "Password Reset", d: "Sends the reset email and walks the caller through the Change password page" },
  ];
}

export default function TscLandingPage({ demo: pageDemo = DEFAULT_TSC_DEMO }: { demo?: TscDemo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Riley, or Mike with ?as=mike (only on a page whose demo has the toggle).
  const demo = tscDemoForAs(pageDemo, params.get("as"));
  const name = demo.persona.name;
  // The banner names every agent on the toggle ("Riley and Mike"), whichever one is selected.
  const everyone = tscVoices(demo).map((d) => d.persona.name).join(" and ") || name;
  const multilingual = demo.languages.length > 1;

  const pick = (d: TscDemo) => {
    const next = new URLSearchParams(location.search);
    const as = tscAsParam(d);
    if (as) next.set("as", as); else next.delete("as");
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  };

  return (
    <div className="tsc-ui">
      <TscHeader demo={demo} />

      <div className="tsc-wrap">
        <div className="tsc-promo"><b>Live demo.</b>{` Talk to ${everyone} in your browser. Every call runs on the real crew and the demo order database.`}</div>
      </div>

      {/* ── Hero ── */}
      <section className="tsc-hero">
        <div className="tsc-wrap tsc-hero-grid">
          <div>
            {tscVoices(demo).length > 1 && <div className="tsc-hero-toggle"><TscAgentToggle demo={demo} onPick={pick} /></div>}
            <div className="tsc-eyebrow">Customer support demo</div>
            <h1 className="tsc-h1">{`Meet ${name}, Tractor Supply's AI voice associate`}</h1>
            <p className="tsc-lead">
              {`${name} verifies the caller, cancels online orders the way the cancellation SOP says, offers one cheaper item to save the sale, `
                + "resets passwords, and transfers anything else to a teammate with a short briefing."}
            </p>
            {multilingual && (
              <p className="tsc-langs">
                Speaks {demo.languages.slice(0, -1).join(", ")} and {demo.languages[demo.languages.length - 1]}
                {`. Start in English and switch whenever you like; ${name} switches with you.`}
              </p>
            )}
            <div className="tsc-hero-ctas">
              <button type="button" className="tsc-btn tsc-btn-primary tsc-btn-lg" onClick={() => navigate(tscPickerHref(demo))}>Pick a customer</button>
            </div>
          </div>
          <div className="tsc-panel" id="crew">
            <div className="tsc-panel-h">{`${name}'s crew`}</div>
            <ol className="tsc-crew">
              {crew(demo).map((a) => (
                <li key={a.name}><span className="tsc-crew-name">{a.name}</span><span className="tsc-crew-d">{a.d}</span></li>
              ))}
              <li className="tsc-crew-human"><span className="tsc-crew-name">Teammate (warm transfer)</span><span className="tsc-crew-d">Any agent can hand the caller to a person. Add your number on the call page to take the transfer yourself.</span></li>
            </ol>
          </div>
        </div>
      </section>

      {/* ── Before you test ── */}
      <section className="tsc-section" id="before">
        <div className="tsc-wrap tsc-two">
          <div className="tsc-panel">
            <div className="tsc-panel-h">Before your session</div>
            <ul className="tsc-list">{beforeSession(demo.testerEmail, demo.persona).map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
          <div className="tsc-panel">
            <div className="tsc-panel-h">On every call</div>
            <ul className="tsc-list">{tscRules(demo.verifyWith, demo.persona).map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        </div>
      </section>

      <TscFooter />
    </div>
  );
}
